import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  Button,
  Divider
} from '@mui/material';
import {
  Dashboard,
  Inventory,
  Business,
  Sync,
  Settings,
  CloudSync,
  Storage
} from '@mui/icons-material';
import DataSourceToggle from './DataSourceToggle';
import StockOwnersManager from './StockOwnersManager';
import SyncManagement from './SyncManagement';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`stock-tabpanel-${index}`}
      aria-labelledby={`stock-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `stock-tab-${index}`,
    'aria-controls': `stock-tabpanel-${index}`,
  };
}

const StockManagement = () => {
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Stock Management System
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your stock with both local MongoDB and Odoo ERP integration
        </Typography>
      </Box>

      {/* Quick Info Banner */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            🔄 Integrated Stock Management Features:
          </Typography>
          <Typography variant="body2">
            • <strong>Switch Data Source:</strong> Toggle between Custom DB and Odoo seamlessly<br />
            • <strong>Manage Stock Owners:</strong> Create and manage ownership entities<br />
            • <strong>Sync Management:</strong> Comprehensive synchronization with Odoo ERP<br />
            • <strong>Product Management:</strong> Enhanced product management with owner assignment
          </Typography>
        </Box>
      </Alert>

      {/* Navigation Tabs */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange} 
            aria-label="stock management tabs"
            variant="fullWidth"
          >
            <Tab 
              label="Data Source" 
              icon={<Settings />} 
              {...a11yProps(0)} 
            />
            <Tab 
              label="Stock Owners" 
              icon={<Business />} 
              {...a11yProps(1)} 
            />
            <Tab 
              label="Sync Management" 
              icon={<Sync />} 
              {...a11yProps(2)} 
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={currentTab} index={0}>
          <Box p={2}>
            <Typography variant="h6" gutterBottom>
              Data Source Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Switch between Custom Database and Odoo ERP as your primary data source. 
              Configure synchronization settings and manage data source priorities.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <DataSourceToggle />
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <Box p={2}>
            <Typography variant="h6" gutterBottom>
              Stock Owners Management
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Create and manage stock ownership entities. Assign products to specific owners 
              and track ownership-based inventory metrics.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <StockOwnersManager />
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <Box p={2}>
            <Typography variant="h6" gutterBottom>
              Synchronization Management
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Manage data synchronization between your local database and Odoo ERP. 
              Set up automatic sync, perform manual synchronization, and monitor sync status.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <SyncManagement />
          </Box>
        </TabPanel>
      </Card>

      {/* Quick Actions Footer */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<CloudSync />}
                onClick={() => setCurrentTab(0)}
                size="large"
              >
                Switch to Odoo
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Storage />}
                onClick={() => setCurrentTab(0)}
                size="large"
              >
                Switch to Custom DB
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Business />}
                onClick={() => setCurrentTab(1)}
                size="large"
              >
                Add Stock Owner
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Sync />}
                onClick={() => setCurrentTab(2)}
                size="large"
              >
                Start Sync
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default StockManagement;